// React & Hooks
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

// Helpers
import { useTranslate } from "helpers";

// Servisni sloj
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";
import {
  deleteVehicleTransferAPI,
  getVehicleLocationsAPI,
} from "services/api/fleet/vehicles/locations/api";
import { VEHICLE_LOCATION_TYPE_CONFIG } from "services/api/fleet/vehicles/locations/consts";
import { VehicleLocation } from "services/api/fleet/vehicles/locations/types";
import { initialFleetVehicleDetailForm } from "services/api/fleet/vehicles/consts";
import {
  FleetVehicleDetailFormValues,
  FleetVehicleStatusEnum,
} from "services/api/fleet/vehicles/types";

// UI
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { NoResults } from "ui/components/NoResults";

// Navigacija
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

// Lokalne komponente
import TabMenu from "ui/modules/fleet/vehicles/pages/components/TabMenu/TabMenu";
import { VehicleTabHeader } from "ui/modules/fleet/vehicles/pages/components/VehicleTabHeader";
import VehicleTransferEditCard from "../VehicleTransferEditCard/VehicleTransferEditCard";
import TransferSearchFilters from "./components/TransferSearchFilters/TransferSearchFilters";
import TransferSearchResults from "./components/TransferSearchResults/TransferSearchResults";

import { initialVehicleTransferSearchForm } from "./consts";
import {
  VehicleTransferSearchFormValues,
  VehicleTransferSearchPageCmp,
  VehicleTransferSearchPageProps,
} from "./types";

/** Granice odabranog dana u milisekundama; `null` kad datum nije odabran. */
const startOfDay = (date: Date | null): number | null =>
  date ? new Date(date).setHours(0, 0, 0, 0) : null;

const endOfDay = (date: Date | null): number | null =>
  date ? new Date(date).setHours(23, 59, 59, 999) : null;

/**
 * Premještaji vozila — gdje je vozilo bilo i kada, tko ga je vozio i zašto.
 * Novi premještaj se pokreće istim čarobnjakom kao i s kartice vozila.
 */
const VehicleTransferSearchPage: VehicleTransferSearchPageCmp = (
  props: VehicleTransferSearchPageProps
) => {
  const { id } = useParams();

  const { t } = useTranslate();

  const [vehicle, setVehicle] = useState<FleetVehicleDetailFormValues>(
    initialFleetVehicleDetailForm
  );
  const [transfers, setTransfers] = useState<VehicleLocation[]>([]);
  const [formValues, setFormValues] = useState<VehicleTransferSearchFormValues>(
    initialVehicleTransferSearchForm
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showTransferModal, setShowTransferModal] = useState<boolean>(false);
  // Premještaj koji se ispravlja; prazno znači unos novog.
  const [editTransfer, setEditTransfer] = useState<VehicleLocation | null>(null);
  // Premještaj koji čeka potvrdu brisanja.
  const [deleteTransferId, setDeleteTransferId] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchVehicle(id);
      fetchTransfers(id);
    }
  }, [id]);

  const fetchVehicle = async (vehicleId: string) => {
    try {
      const result = await getFleetVehicleAPI(vehicleId);
      setVehicle(result);
    } catch {
      setVehicle(initialFleetVehicleDetailForm);
    }
  };

  const fetchTransfers = async (vehicleId: string) => {
    setIsLoading(true);
    try {
      const results = await getVehicleLocationsAPI(vehicleId);
      setTransfers(results);
    } catch {
      setTransfers([]);
    }
    setIsLoading(false);
  };

  const handleSearchChange = useCallback(
    (values: VehicleTransferSearchFormValues) => {
      setFormValues(values);
    },
    []
  );

  const handleTransferClick = useCallback(() => {
    setEditTransfer(null);
    setShowTransferModal(true);
  }, []);

  // Klik na red otvara isti čarobnjak, ali u načinu ispravka postojećeg premještaja.
  const handleRowClick = useCallback(
    (transferId: string) => {
      const found = transfers.find((transfer) => transfer.id === transferId) ?? null;

      if (!found) return;

      setEditTransfer(found);
      setShowTransferModal(true);
    },
    [transfers]
  );

  const handleTransferClose = useCallback(() => {
    setShowTransferModal(false);
    setEditTransfer(null);
  }, []);

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, transferId: string) => {
      event.stopPropagation();
      event.preventDefault();

      setDeleteTransferId(transferId);
    },
    []
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!id || !deleteTransferId) return;

    try {
      await deleteVehicleTransferAPI(id, deleteTransferId, t);
      // Brisanje aktivnog premještaja vraća vozilo na prethodnu lokaciju, pa se uz popis
      // osvježava i vozilo u zaglavlju.
      fetchTransfers(id);
      fetchVehicle(id);
    } catch (err) {
      console.error("Greška prilikom brisanja premještaja:", err);
    }

    setDeleteTransferId(null);
  }, [id, deleteTransferId, t]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteTransferId(null);
  }, []);

  const handleTransferSuccess = useCallback(() => {
    if (id) fetchTransfers(id);
  }, [id]);

  // Zaglavlje pokazuje status vozila, pa se nakon promjene kroz premještaj dohvaća ispočetka.
  const handleTransferStatusChange = useCallback(() => {
    if (id) fetchVehicle(id);
  }, [id]);

  // Filtriranje je na klijentu jer endpoint vraća cijelu povijest bez parametara.
  const filteredTransfers = useMemo(() => {
    const search = formValues.search.trim().toLowerCase();

    // Raspon se uspoređuje po danu — vrijeme premještaja bi inače izbacilo
    // premještaj koji pada točno na granični datum.
    const dateFrom = startOfDay(formValues.validFromFrom);
    const dateTo = endOfDay(formValues.validFromTo);

    return transfers.filter((transfer) => {
      if (formValues.locationType && transfer.locationType !== formValues.locationType) {
        return false;
      }

      if (formValues.isActive !== null && transfer.isActive !== formValues.isActive) {
        return false;
      }

      if (dateFrom || dateTo) {
        if (!transfer.validFrom) return false;

        const validFrom = new Date(transfer.validFrom).getTime();

        if (dateFrom && validFrom < dateFrom) return false;
        if (dateTo && validFrom > dateTo) return false;
      }

      if (!search) return true;

      const haystack = [
        transfer.name,
        transfer.locationName,
        transfer.driverName,
        transfer.note,
        transfer.locationType
          ? VEHICLE_LOCATION_TYPE_CONFIG[transfer.locationType]?.label
          : null,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(search);
    });
  }, [transfers, formValues]);

  if (!id) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <VehicleTabHeader
            vehicleId={id}
            vehicle={vehicle}
            title="Transferi"
            icon="/media/icons/location.svg"
            iconColor="stroke-[#edeef8]"
            onStatusChange={handleTransferStatusChange}
          >
            <Button
              variant="primary"
              children="Premjesti vozilo"
              startIcon="/media/icons/plus.svg"
              onClick={handleTransferClick}
            />
          </VehicleTabHeader>
        </div>
        <TabMenu vehicleId={id} />
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <TransferSearchFilters
            formValues={formValues}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && filteredTransfers.length === 0 ? (
            <NoResults message={t("admin.fleet.vehicle.transfer.search.noresults")} />
          ) : (
            <TransferSearchResults
              transfers={filteredTransfers}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onDeleteClick={handleDeleteClick}
            />
          )}
        </div>
      </div>

      <VehicleTransferEditCard
        show={showTransferModal}
        vehicleId={id}
        currentLocation={transfers.find((transfer) => transfer.isActive) ?? null}
        currentStatus={vehicle.status as FleetVehicleStatusEnum | null}
        transfer={editTransfer}
        onSuccess={handleTransferSuccess}
        onStatusChange={handleTransferStatusChange}
        onClose={handleTransferClose}
      />

      <Dialog
        open={!!deleteTransferId}
        title="Brisanje premještaja"
        message="Jeste li sigurni da želite obrisati premještaj? Prethodni premještaj preuzima njegovo razdoblje."
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};

VehicleTransferSearchPage.route =
  FleetVehicleRoutes.FleetVehicleTransferSearchPageRouter;

export default VehicleTransferSearchPage;
